import React, { Dispatch, useEffect, useState } from 'react';

import { CustomerTypeEnum, PaperPlanMetadata } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import Alert from '@amzn/meridian/alert';
import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';

import { isEmpty } from 'lodash';
import { ContractTermSection } from 'src/components/shared/ContractTermSection';
import { IPrimeAMFRateCardValidationAction } from 'src/types/IPrimeAMFRateCardValidationAction';
import { IPrimeAMFRateCardValuesAction } from 'src/types/IPrimeAMFRateCardValuesAction';
import { IPrimeAMFRateCardValues } from 'src/types/IRateCard';
import { getCamelStrFromAllCaps } from 'src/util/RateCards';

import { IPrimeAMFRateCardErrors } from '../../rateCardValidationReducer';

interface IAddCustomerType {
    rateCardValues: IPrimeAMFRateCardValues;
    dispatchRateCardValues: Dispatch<IPrimeAMFRateCardValuesAction>;
    rateCardErrors: IPrimeAMFRateCardErrors;
    dispatchRateCardErrors: Dispatch<IPrimeAMFRateCardValidationAction>;
    customerTypes: PaperPlanMetadata[];
    isMAU: boolean; // Determines if component is CPP or MAU
}

export const AddCustomerType: React.FC<IAddCustomerType> = ({
    rateCardValues,
    dispatchRateCardValues,
    rateCardErrors,
    dispatchRateCardErrors,
    customerTypes,
    isMAU
}) => {
    // Get customer type from either CPP or MAU object
    const customerTypeList =
        (isMAU
            ? rateCardValues.primeContractTerms?.monthlyActiveUser?.customerType
            : rateCardValues.primeContractTerms?.costPerPlay?.customerTypes) ?? [];
    const hasCustomerTypes = !isEmpty(customerTypeList);
    const [isToggled, setIsToggled] = useState(hasCustomerTypes);
    const errorMessage = isMAU
        ? rateCardErrors.primeContractTerms.monthlyActiveUser.customerTypes
        : rateCardErrors.primeContractTerms.costPerPlay.customerTypes;

    // Validate on rate card/toggle change
    useEffect(() => {
        dispatchRateCardErrors({
            type: 'validate_customer_type',
            rateCardValues,
            isToggled,
            isMAU
        });
    }, [
        rateCardValues.primeContractTerms?.costPerPlay?.customerTypes,
        rateCardValues.primeContractTerms?.monthlyActiveUser?.customerType
    ]);

    const containsCustomerType = (customerTypeName: string): boolean => {
        return customerTypeList.some((customerType) => customerType === customerTypeName);
    };

    const handleToggle = (): void => {
        if (isToggled) {
            dispatchRateCardValues({
                type: 'reset_customer_types',
                isMAU
            });
        }

        setIsToggled((prevIsToggled) => !prevIsToggled);
    };

    const handleCustomerTypeChange = (customerTypeName: CustomerTypeEnum, shouldBeChecked: boolean): void => {
        dispatchRateCardValues({
            type: 'set_customer_type',
            customerType: customerTypeName,
            shouldBeIncluded: shouldBeChecked,
            isMAU: isMAU
        });
    };

    return (
        <ContractTermSection
            title="Customer type"
            subTitle="Enable different rates per customer type (Paid, Free-Trial, Preview, etc.)"
            isToggled={isToggled}
            handleToggle={handleToggle}
        >
            {isToggled && (
                <>
                    <Column spacing="300">
                        <Text type="h300">Select customer types</Text>
                        {customerTypes.map((customerType) => (
                            <Checkbox
                                key={customerType.paperPlan?.paperPlanId}
                                data-testid={customerType.paperPlan?.paperPlanName}
                                checked={containsCustomerType(customerType.paperPlan!.displayName!)}
                                onChange={(val) =>
                                    handleCustomerTypeChange(
                                        customerType.paperPlan!.paperPlanName as CustomerTypeEnum,
                                        val
                                    )
                                }
                            >
                                {getCamelStrFromAllCaps(customerType.paperPlan!.displayName!)}
                            </Checkbox>
                        ))}
                    </Column>
                    {!isEmpty(errorMessage) && (
                        <Alert type="error" size="small">
                            {errorMessage}
                        </Alert>
                    )}
                </>
            )}
        </ContractTermSection>
    );
};
